import { type PropsWithChildren } from "react";
import {
  CoreAlert,
  type AlertSeverity,
  type HeadingLevel,
  type PropsWithOptionalTestID,
} from "@boi/components-core";
import { useDesignTokens } from "@boi/react-design-tokens";
import {
  ErrorIcon,
  WarningIcon,
  SuccessIcon,
  InfoIcon,
  CloseIcon,
} from "@boi/react-icons";

import * as S from "./Alert.styled";
import { Typography } from "../Typography";
import { IconButtonV2 } from "../IconButtonV2";

export type AlertProps = PropsWithOptionalTestID &
  PropsWithChildren<{
    severity?: AlertSeverity;
    heading: string;
    headingLevel?: HeadingLevel;
    onRequestClose?: () => void;
    withRole?: boolean;
  }>;

function Alert(props: AlertProps) {
  const {
    severity = CoreAlert.DEFAULT_SEVERITY,
    heading,
    headingLevel = CoreAlert.DEFAULT_HEADING_LEVEL,
    onRequestClose,
    withRole = true,
    children,
    testID = "alert",
  } = props;

  const { tokens } = useDesignTokens();
  const { alert } = tokens;

  const hasBody = Boolean(children);
  const isDismissible = Boolean(onRequestClose);

  const alertIcon = {
    error: <ErrorIcon />,
    warning: <WarningIcon />,
    success: <SuccessIcon />,
    info: <InfoIcon />,
  };

  return (
    <S.Container
      role={withRole ? "alert" : undefined}
      $severity={severity}
      data-testid={testID}
    >
      <S.ContentContainer $isDismissible={isDismissible}>
        <S.IconWrapper $severity={severity}>
          {alertIcon[severity]}
        </S.IconWrapper>

        <S.TextContainer>
          <Typography
            component={`h${headingLevel}`}
            color={alert.heading.typography.color}
            variant={alert.heading.typography.variant}
            fontFamily={alert.heading.typography.fontFamily}
            data-testid={`${testID}-heading`}
          >
            {heading}
          </Typography>

          {children && (
            <Typography
              component="div"
              color={alert.body.typography.color}
              variant={alert.body.typography.variant}
              fontFamily={alert.body.typography.fontFamily}
              data-testid={`${testID}-body`}
            >
              {children}
            </Typography>
          )}
        </S.TextContainer>
      </S.ContentContainer>

      {isDismissible && (
        <S.CloseIconButtonWrapper $hasBody={hasBody}>
          <IconButtonV2 onPress={onRequestClose} aria-label="Close">
            <CloseIcon />
          </IconButtonV2>
        </S.CloseIconButtonWrapper>
      )}
    </S.Container>
  );
}

export default Alert;
